import {Injectable}                                         from '@angular/core';
import {BestScanData}                                       from '../../types/scan.types';
import {ApplicationSettingsService}                         from './application-settings.service';
import {ApTranslationService}                               from '../../ap-utils/service/ap-translation.service';
import {ApStringService}                                    from '../../ap-utils/service/ap-string.service';
import {NotifyStore}                                        from '../../stores/dialog/notify.store';
import {OperationModesEnum}                                 from '../../stores/common/operation-modes.store';
import {ApplicationModesEnum}                               from '../../stores/common/appl-modes.store';
import {
  AreaWeightedAverageOfScansData,
  FieldScanData,
  GetGrowthStageData
}                                                           from '../../types/n-fertilization-plant-protection.types';
import {
  PpConstants
}                                                           from '../../entry-components/plant-protection-entry-components/planning-wizard/pp-planning-wizard.types';
import {Create}                                             from 'ts-tooling';
import {ApColumnState}                                      from '../../ap-interface/enums/ap-column-state.enum';
import {
  IGridStatus
}                                                           from '../../ap-interface/interfaces/ap-grid-status.interface';
import {AreaWeightedAverageData}                            from '../../types/common-types';
import IApplicationSettings = Data.Agronomic.IApplicationSettings;
import ApValidationLevel = Data.Api.Validation.ApValidationLevel;
import IApValidationResult = Data.Api.Validation.IApValidationResult;

@Injectable({providedIn: 'root'})
export class NFertilizationPlantProtectionService {
  constructor(private notifyStore: NotifyStore,
              private stringService: ApStringService,
              private translationService: ApTranslationService,
              private applicationSettingsService: ApplicationSettingsService) {
  }

  /**
   * Determines the grid status based on the operation mode
   * Returns yellow status for constant operation mode and green status for variable modes
   * @param operationModeEnum Operation mode to determine status for
   * @returns Grid status object with state, class and description
   * @private
   */
  public getStatusForItemOnOverviewStep(operationModeEnum: OperationModesEnum): IGridStatus {
    if (operationModeEnum === OperationModesEnum.Constant) {
      return {
        state: ApColumnState.Yellow,
        class: 'ap-status-yellow',
        description: 'Global__RateConst'
      };
    }
    return {
      state: ApColumnState.Green,
      class: 'ap-status-green',
      description: 'Global__RateVariable'
    };
  }

  /**
   * Converts operation mode to corresponding application mode
   * @param operationModeKey The current operation mode
   * @returns Corresponding application mode
   * @public
   */
  public getApplicationModeFromOperationMode(operationModeKey: OperationModesEnum | undefined): ApplicationModesEnum {
    if (!operationModeKey) {
      return undefined;
    }
    if (operationModeKey === OperationModesEnum.Constant) {
      return ApplicationModesEnum.Constant;
    }
    return ApplicationModesEnum.Variable;
  }

  /**
   * Determines the appropriate operation mode for the given plan based on the provided operation mode,
   * field scan data, and application settings.
   * @param operationMode - The current operation mode to be evaluated.
   * @param fieldsScanData - Data related to the field scan, including scan status and metrics.
   * @param applicationSettings - Optional application-specific settings used to validate the operation mode.
   * @return The determined operation mode based on the evaluation of input parameters.
   * @public
   */
  public getOperationModeForPlan(operationMode: OperationModesEnum, fieldsScanData: FieldScanData,
                                 applicationSettings: IApplicationSettings | undefined): OperationModesEnum {
    if (applicationSettings && this.isOperationModeAnyOfMaps(operationMode)) {
      const isApplicationSettingValidForGrowthStage = this.applicationSettingsService.isApplicationSettingValidForGrowthStage(
        fieldsScanData.ScanData.Ec, applicationSettings);
      return fieldsScanData.FieldStatus === ApColumnState.Green && isApplicationSettingValidForGrowthStage
        ? operationMode
        : OperationModesEnum.Constant;
    }
    return this.isOperationModeAnyOfMapsOrConstant(operationMode) && fieldsScanData.ScanData.Status.state !== ApColumnState.Green
      ? OperationModesEnum.Constant
      : operationMode;
  }

  /**
   * Determines the application mode for a given plan based on the operation mode,
   * application mode, and fields scan data.
   * @param operationMode - The operation mode defining the current state of operation.
   * @param applicationMode - The application-mode value that may be returned if conditions are met.
   * @param fieldsScanData - An object containing field status and scan data information.
   * @return The resulting application mode based on the specified conditions.
   * @public
   */
  public getApplicationModeForPlan(operationMode: OperationModesEnum, applicationMode: ApplicationModesEnum,
                                   fieldsScanData: FieldScanData): ApplicationModesEnum {
    if (operationMode === OperationModesEnum.Constant
      || (this.isOperationModeAnyOfMaps(operationMode)
        && (fieldsScanData.FieldStatus !== ApColumnState.Green || fieldsScanData.ScanData.Status.state !== ApColumnState.Green))) {
      return ApplicationModesEnum.Constant;
    }
    return applicationMode;
  }

  /**
   * Checks if the operation mode is a map-based or constant mode
   * @param operationMode Operation mode to check
   * @returns Boolean indicating if mode is map-based or constant
   * @public
   */
  public isOperationModeAnyOfMapsOrConstant(operationMode: OperationModesEnum): boolean {
    if (this.isOperationModeAnyOfMaps(operationMode)) {
      return true;
    }
    return operationMode === OperationModesEnum.Constant;
  }

  /**
   * Checks if the operation mode is an application map or absolute mode
   * @param operationModeKey Operation mode to check
   * @returns Boolean indicating if mode is an application map or absolute mode
   * @public
   */
  public isOperationModeAnyOfMaps(operationModeKey: OperationModesEnum): boolean {
    if (!operationModeKey) {
      return false;
    }
    return operationModeKey === OperationModesEnum.ApplicationMapAbsolute
      || operationModeKey === OperationModesEnum.ApplicationMap
      || operationModeKey === OperationModesEnum.NfTargetMapPerPlan
      || operationModeKey === OperationModesEnum.PpTargetMapPerField
      || operationModeKey === OperationModesEnum.PPTargetMapPerPlan;
  }

  /**
   * Checks if the provided operation mode key matches any of the predefined sensor operation modes.
   * @param operationModeKey - The operation mode key to evaluate, represented as an enum value from OperationModesEnum.
   * @return Returns true if the operation mode key matches NFertilization, TargetValue, or RapeFertilization; otherwise, returns false.
   * @public
   */
  public isOperationModeAnyOfSensor(operationModeKey: OperationModesEnum): boolean {
    if (!operationModeKey) {
      return false;
    }
    return operationModeKey === OperationModesEnum.NFertilization
      || operationModeKey === OperationModesEnum.TargetValue
      || operationModeKey === OperationModesEnum.RapeFertilization;
  }

  /**
   * Processes the text warning for application settings validation based on the fields' scan data.
   * @param fieldsScanData - An array of field scan data objects to be validated. Must not be null or empty.
   * @param applicationSettings - The application settings used for validation. Must not be null.
   * @return No return value. Adds a warning message to the notification store when necessary.
   * @public
   */
  public onApplicationSettingsTextWarning(fieldsScanData: FieldScanData[], applicationSettings: IApplicationSettings): void {
    if (!fieldsScanData || fieldsScanData.length <= 0 || !applicationSettings) {
      return;
    }
    let warningText: string;
    const validFieldsScanData = fieldsScanData.filter(x => {
      return x.FieldStatus === ApColumnState.Green
        && this.applicationSettingsService.isApplicationSettingValidForGrowthStage(x.ScanData.Ec, applicationSettings);
    });
    if (validFieldsScanData.length === 0) {
      warningText = this.translationService.translate('AP_TK__Plan_Creation_Not_Allowed');
    } else if (fieldsScanData.length !== validFieldsScanData.length) {
      const translatedText = this.translationService.translate('PlanCreationOutsideOfLimitation');
      warningText = this.stringService.format(translatedText, validFieldsScanData.length, fieldsScanData.length);
    } else {
      return;
    }
    this.notifyStore.addMessage({
      Level: ApValidationLevel.Warning,
      Counter: 1,
      ErrorKey: warningText,
      DisplayTimeout: 2
    } as IApValidationResult);
  }

  /**
   * Filters and retrieves the best scan data entries that are valid based on the provided application settings.
   * @param fieldsScanData - An array of field scan data objects that need to be evaluated.
   * @param applicationSettings - The application settings used to validate the scan data.
   * @return An array of scan data objects that are considered valid based on the given criteria.
   * @public
   */
  public getValidBestScansByApplicationSettings(fieldsScanData: FieldScanData[],
                                                applicationSettings: IApplicationSettings): BestScanData[] {
    return fieldsScanData?.filter(x => {
      return x.FieldStatus === ApColumnState.Green
        && x.ScanData.Status.state === ApColumnState.Green
        && this.applicationSettingsService.isApplicationSettingValidForGrowthStage(x.ScanData.Ec, applicationSettings);
    })?.map(x => x.ScanData) ?? [];
  }

  /**
   * Extracts unique growth stages from items
   * @param items Array of growth stage data
   * @returns Array of unique EC values
   * @public
   */
  public getGrowStages(items: GetGrowthStageData[]): number[] {
    const ec: number[] = [];
    items.map(item => {
      if (item?.ScanData?.Ec != null && item.ScanData.Ec >= 0
        && ec.every(x => x !== item.ScanData.Ec)) {
        ec.push(item.ScanData.Ec);
      }
      if (item?.CropData?.EcValue != null && item.CropData.EcValue >= 0
        && ec.every(x => x !== item.CropData.EcValue)) {
        ec.push(item.CropData.EcValue);
      }
    });
    return ec;
  }

  /**
   * Calculates a slope based on operation mode and other parameters
   * @param operationModeKey Current operation mode
   * @param controlFactor Slope value
   * @param isRape Flag indicating if the crop is rapeseed
   * @param applicationSettings Application-specific settings
   * @returns Calculated slope value
   * @public
   */
  public getSlope(operationModeKey: OperationModesEnum, controlFactor: number | undefined,
                  isRape: boolean, applicationSettings: IApplicationSettings | undefined): number {
    if (!Number.isFinite(controlFactor)) {
      controlFactor = applicationSettings?.Parameter?.slope?.FirstOrDefault()?.Slope ?? 1;
    }
    if (applicationSettings && this.isOperationModeAnyOfMaps(operationModeKey)) {
      return controlFactor;
    }
    return operationModeKey === OperationModesEnum.Constant
      ? 0
      : isRape ? 1 : controlFactor;
  }

  /**
   * Calculates the area-weighted average of valid scans.
   * Filters out invalid scans and computes the weighted average
   * based on the area and nitrogen uptake average of each scan.
   * @param items Array of items containing area and scan data
   * @param applicationSettings Optional application settings for filtering
   * @returns Calculated area-weighted average of valid scans
   * @public
   */
  public calculateAreaWeightedAverageOfScans(items: AreaWeightedAverageOfScansData[], applicationSettings?: IApplicationSettings): number {
    let filteredItems = items.filter(x => Number.isFinite(x.ScanData?.NUptakeAvg));
    if (!!applicationSettings) {
      filteredItems = filteredItems.filter(x => {
        return this.isValidScanData(x.ScanData)
          && this.applicationSettingsService.isApplicationSettingValidForGrowthStage(x.ScanData.Ec, applicationSettings);
      });
    }
    const totalArea = filteredItems.reduce((areaSum, item) => areaSum + Create(item?.Area, 0), 0);
    const averageWithFieldsArea = filteredItems.reduce((sensorAvgSum, item) => {
      const sensorAvg = item.ScanData.NUptakeAvg > -1 ? item.ScanData.NUptakeAvg * Create(item?.Area, 0) : 0;
      return sensorAvgSum + sensorAvg;
    }, 0);
    if (!averageWithFieldsArea || !totalArea) {
      return 0;
    }
    return averageWithFieldsArea / totalArea;
  }

  /**
   * Calculates the area-weighted average of the provided items
   * @param items Array of items with area and average values
   * @returns Calculated area-weighted average
   * @public
   */
  public calculateAreaWeightedAverage(items: AreaWeightedAverageData[]): number {
    const filteredItems = items.filter(x => Number.isFinite(x.Average));
    const totalArea = filteredItems.reduce((areaSum, item) => areaSum + Create(item?.Area, 0), 0);
    const averageWithFieldsArea = filteredItems.reduce((sensorAvgSum, item) => {
      const sensorAvg = item.Average > -1 ? item.Average * Create(item?.Area, 0) : 0;
      return sensorAvgSum + sensorAvg;
    }, 0);
    if (!averageWithFieldsArea || !totalArea) {
      return 0;
    }
    return averageWithFieldsArea / totalArea;
  }

  /**
   * Validates if the provided scan data has a Green status.
   * A Green status indicates that the scan data is valid for further processing.
   * @param scanData Best scan data to validate
   * @returns True if the scan data has a Green status, otherwise false
   * @public
   */
  public isValidScanData(scanData: BestScanData): boolean {
    return scanData?.Status?.state === ApColumnState.Green;
  }

  /**
   * Generates a human-readable description of the control factor based on operation mode
   * Handles different scenarios:
   * - For application settings based operation modes
   * - For constant mode, returns a constant rate description
   * - For other modes, uses predefined control factor constants
   * @param operationMode The current operation mode (e.g., Constant, Target Map)
   * @param slope Numeric slope value used for determining the description
   * @param applicationSettings Optional settings that may influence the description
   * @param isPlantProtection Flag to differentiate between plant protection and other applications
   * @returns Translated string describing the control factor, or '-' if no description available
   * @public
   */
  public getControlFactorDescription(operationMode: OperationModesEnum, slope: number,
                                     applicationSettings: IApplicationSettings | undefined,
                                     isPlantProtection: boolean): string {
    if ((this.isOperationModeAnyOfMaps(operationMode) || this.isOperationModeAnyOfSensor(operationMode))
      && !!applicationSettings) {
      const parameters = applicationSettings.Parameter;
      if (!parameters || !parameters.slope?.Any()) {
        return '-';
      }
      const slopeKey = parameters.slope.FirstOrDefault(s => s.Slope === slope)?.Key;
      if (parameters.slopeType === 'dropDown' && !!slopeKey) {
        return this.translationService.translate(slopeKey);
      }
      return '-';
    } else if (operationMode === OperationModesEnum.Constant) {
      return this.translationService.translate('Global__RateConst');
    } else {
      const defaultSlopeKey = this.getDefaultSlopeKey(slope, isPlantProtection);
      if (!!defaultSlopeKey) {
        return this.translationService.translate(defaultSlopeKey);
      }
      return '-';
    }
  }

  /**
   * Retrieves the default slope key based on a type of application
   * @param slope Slope value
   * @param isPlantProtection Flag indicating if it's plant protection
   * @returns Translated slope key
   * @private
   */
  private getDefaultSlopeKey(slope: number, isPlantProtection: boolean): string {
    if (isPlantProtection) {
      return PpConstants.PpControlFactors.FirstOrDefault(c => c.Id === slope)?.Key;
    } else {
      return slope === -1 ? 'Global__Quality_Oriented' : 'Global__Yield_Oriented';
    }
  }
}
